<template>
  <div>
    <v-btn @click="dialog = true" text small color="red darken-3">
      <v-badge color="red" :content="pending_units.length" v-if="pending_units">
        <v-icon class="mr-1">mdi-laptop-account</v-icon>Client Units</v-badge
      >
      <span v-else> Client Units</span>
    </v-btn>

    <v-dialog v-model="dialog" persistent width="1250" v-if="pending_units">
      <v-card>
        <ModalHeader
          title="Client Units"
          icon="mdi-laptop"
          @close="dialog = false"
        />
        <v-card-text class="container pa-4">
          <div v-if="ready">
            <v-row align="center" class="mt-0">
              <v-col cols="12" md="4" class="py-1">
                <v-text-field
                  v-model="search"
                  prepend-icon="mdi-magnify"
                  hide-details
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="6" md="3" class="py-1">
                <v-text-field
                  v-model="date_from"
                  label="Date From"
                  type="date"
                  outlined
                  dense
                  hide-details
                  clearable
                ></v-text-field>
              </v-col>
              <v-col cols="6" md="3" class="py-1">
                <v-text-field
                  v-model="date_to"
                  label="Date To"
                  type="date"
                  outlined
                  dense
                  hide-details
                  clearable
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="2" class="py-1 d-flex align-center">
                <v-btn
                  v-if="date_from || date_to"
                  text
                  small
                  color="grey darken-1"
                  @click="clear_dates"
                  ><v-icon class="mr-1">mdi-filter-remove</v-icon>Clear</v-btn
                >
                <v-btn
                  text
                  color="green"
                  small
                  :loading="loading"
                  @click="refresh"
                  ><v-icon class="mr-1">mdi-refresh</v-icon>Refresh</v-btn
                >
              </v-col>
            </v-row>

            <v-tabs v-model="tab" color="primary">
              <v-tab>Pending ({{ filtered_pending_units.length }})</v-tab>
              <v-tab
                >Work in Progress ({{ filtered_wip_units.length }})</v-tab
              >
              <v-tab>Picked-up ({{ filtered_pickup_units.length }})</v-tab>
              <v-tab>Cancelled ({{ filtered_cancelled_units.length }})</v-tab>
            </v-tabs>
            <v-tabs-items v-model="tab">
              <v-tab-item>
                <v-data-table
                  :headers="headers"
                  :items="filtered_pending_units"
                  :search="search"
                >
                  <template v-slot:item.action="{ item }">
                    <div class="d-flex">
                      <v-btn
                        @click="show_problem(item)"
                        color="primary"
                        small
                        icon
                        class="mt-1"
                        ><v-icon>mdi-list-box</v-icon></v-btn
                      >
                      <v-btn
                        @click="start_working(item)"
                        color="orange"
                        small
                        icon
                        class="mt-1"
                        title="Start Working (Technician/Admin only)"
                        :disabled="!can_start_working"
                        :loading="starting_id == item.id"
                        ><v-icon>mdi-wrench</v-icon></v-btn
                      >
                      <v-btn
                        @click="ready_for_pickup(item)"
                        color="success"
                        small
                        icon
                        class="mt-1"
                        title="Ready for Pickup"
                        :loading="pickup_id == item.id"
                        ><v-icon>mdi-thumb-up</v-icon></v-btn
                      >
                    </div>
                  </template>
                  <template v-slot:item.status="{ item }">
                    <p class="orange--text darken-4">{{ item.status }}</p>
                  </template>
                </v-data-table>
              </v-tab-item>
              <v-tab-item>
                <v-data-table
                  :headers="wip_headers"
                  :items="filtered_wip_units"
                  :search="search"
                  sort-by="id"
                  :sort-desc="true"
                >
                  <template v-slot:item.action="{ item }">
                    <div class="d-flex">
                      <v-btn
                        @click="show_problem(item)"
                        color="primary"
                        small
                        icon
                        class="mt-1"
                        ><v-icon>mdi-list-box</v-icon></v-btn
                      >
                      <v-btn
                        @click="ready_for_pickup(item)"
                        color="success"
                        small
                        icon
                        class="mt-1"
                        title="Ready for Pickup"
                        :loading="pickup_id == item.id"
                        ><v-icon>mdi-thumb-up</v-icon></v-btn
                      >
                    </div>
                  </template>
                  <template v-slot:item.status="{ item }">
                    <p class="blue--text darken-4">{{ item.status }}</p>
                  </template>
                </v-data-table>
              </v-tab-item>
              <v-tab-item>
                <v-data-table
                  :headers="pickup_headers"
                  :items="filtered_pickup_units"
                  :search="search"
                  sort-by="id"
                  :sort-desc="true"
                >
                  <template v-slot:item.action="{ item }">
                    <div class="d-flex">
                      <v-btn
                        @click="show_problem(item)"
                        color="primary"
                        small
                        icon
                        class="mt-1"
                        ><v-icon>mdi-list-box</v-icon></v-btn
                      >
                    </div>
                  </template>
                  <template v-slot:item.status="{ item }">
                    <p class="green--text darken-4">{{ item.status }}</p>
                  </template>
                </v-data-table>
              </v-tab-item>
              <v-tab-item>
                <v-data-table
                  :headers="cancelled_headers"
                  :items="filtered_cancelled_units"
                  :search="search"
                  sort-by="id"
                  :sort-desc="true"
                >
                  <template v-slot:item.action="{ item }">
                    <div class="d-flex">
                      <v-btn
                        @click="show_problem(item)"
                        color="primary"
                        small
                        icon
                        class="mt-1"
                        ><v-icon>mdi-list-box</v-icon></v-btn
                      >
                    </div>
                  </template>
                  <template v-slot:item.status="{ item }">
                    <p class="red--text darken-4">{{ item.status }}</p>
                  </template>
                </v-data-table>
              </v-tab-item>
            </v-tabs-items>
            <problem-layout-modal
              class="hidden-md-and-down"
              :problem="selected_problem"
              v-if="show_problem_layout"
              @close="show_problem_layout = false"
            />
            <problem-layout-modal-mobile
              class="hidden-md-and-up"
              :problem="selected_problem"
              v-if="show_problem_layout"
              @close="show_problem_layout = false"
            />
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn @click="dialog = false"> Close</v-btn>
          <v-spacer></v-spacer>
          <p class="caption grey--text">pendingUnits.vue</p>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

export default {
  components: {
    ProblemLayoutModal: () => import("../Problems/problemLayoutModal.vue"),
    ProblemLayoutModalMobile: () =>
      import("../Problems/problemLayoutModalMobile.vue"),
  },
  data() {
    return {
      ready: false,
      tab: 0,
      search: "",
      date_from: "",
      date_to: "",
      selected_problem: [],
      show_problem_layout: false,
      headers: [
        { text: "", value: "action" },
        { text: "ID", value: "id" },
        { text: "Customer Name", value: "unit.customer.customer_name" },
        { text: "Problem Description", value: "problem_description" },
        { text: "Unit Brand", value: "unit.unit_brand" },
        { text: "Unit Model", value: "unit.unit_model" },
        { text: "Type", value: "unit.unit_type" },
        { text: "Date Inserted", value: "created_at" },
        { text: "Last Update", value: "updated_at" },
        { text: "Status", value: "status" },
      ],
      wip_headers: [
        { text: "", value: "action" },
        { text: "ID", value: "id" },
        { text: "Customer Name", value: "unit.customer.customer_name" },
        { text: "Problem Description", value: "problem_description" },
        { text: "Technician", value: "technician" },
        { text: "Unit Brand", value: "unit.unit_brand" },
        { text: "Unit Model", value: "unit.unit_model" },
        { text: "Type", value: "unit.unit_type" },
        { text: "Date Inserted", value: "created_at" },
        { text: "Last Update", value: "updated_at" },
        { text: "Status", value: "status" },
      ],
      pickup_headers: [
        { text: "", value: "action" },
        { text: "ID", value: "id" },
        { text: "Customer Name", value: "unit.customer.customer_name" },
        { text: "Problem Description", value: "problem_description" },
        { text: "Technician", value: "technician" },
        { text: "Unit Brand", value: "unit.unit_brand" },
        { text: "Unit Model", value: "unit.unit_model" },
        { text: "Type", value: "unit.unit_type" },
        { text: "Picked Up By", value: "picked_up_by" },
        { text: "Picked Up At", value: "picked_up_at" },
        { text: "Last Update", value: "updated_at" },
        { text: "Status", value: "status" },
      ],
      cancelled_headers: [
        { text: "", value: "action" },
        { text: "ID", value: "id" },
        { text: "Customer Name", value: "unit.customer.customer_name" },
        { text: "Problem Description", value: "problem_description" },
        { text: "Technician", value: "technician" },
        { text: "Unit Brand", value: "unit.unit_brand" },
        { text: "Unit Model", value: "unit.unit_model" },
        { text: "Type", value: "unit.unit_type" },
        { text: "Date Inserted", value: "created_at" },
        { text: "Last Update", value: "updated_at" },
        { text: "Status", value: "status" },
      ],
      dialog: false,
      loading: false,
      problem_dialog: false,
      starting_id: null,
      pickup_id: null,
    };
  },
  methods: {
    ...mapActions({
      get_unit_status: "unit/get_unit_status",
      set_problem: "problem/set_problem",
      get_pending_units: "unit/get_pending_units",
      get_wip_units: "unit/get_wip_units",
      get_pickup_units: "unit/get_pickup_units",
      get_cancelled_units: "unit/get_cancelled_units",
      start_working_unit: "unit/start_working",
      ready_for_pickup_unit: "unit/ready_for_pickup",
    }),
    async start_working(item) {
      if (!this.can_start_working) {
        this.$alert({
          title: "Not Allowed",
          message:
            "Only Technician and Admin accounts can start working on a unit.",
          color: "error",
          icon: "mdi-alert-circle",
        });
        return;
      }
      let x = await this.$confirm({
        title: "Start Working",
        message:
          "Start working on this unit? Its status will change to Work on Process, you will be assigned as its technician, and the owner will be notified via text message.",
        color: "orange",
        icon: "mdi-wrench",
      });
      if (!x) return;
      this.starting_id = item.id;
      try {
        await this.start_working_unit(item);
        this.$alert({
          title: "Success",
          message: "Unit is now marked as Work on Process",
          color: "success",
          icon: "mdi-check-circle",
        });
      } catch (err) {
        this.$alert({
          title: "Not Allowed",
          message:
            (err.response && err.response.data && err.response.data.message) ||
            String(err),
          color: "error",
          icon: "mdi-alert-circle",
        });
      } finally {
        this.starting_id = null;
      }
    },
    parse_list(value) {
      if (!value) return [];
      if (Array.isArray(value)) return value;
      try {
        let parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    },
    escape_html(str) {
      return String(str)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    },
    async ready_for_pickup(item) {
      let actions = this.parse_list(item.actions_performed);
      let remarks = this.parse_list(item.other_remarks);
      if (actions.length === 0 && remarks.length === 0) {
        this.$alert({
          title: "Action or Remarks Required",
          message:
            "This unit has no recorded actions or remarks yet. Open the unit details (list icon) and add at least one action performed or remark before marking it as Ready to Pick-up.",
          color: "error",
          icon: "mdi-alert-circle",
        });
        return;
      }
      let details = "";
      if (actions.length > 0) {
        details +=
          "<b>Actions Performed:</b><ul style='margin-bottom:8px'>" +
          actions.map((a) => "<li>" + this.escape_html(a) + "</li>").join("") +
          "</ul>";
      }
      if (remarks.length > 0) {
        details +=
          "<b>Remarks:</b><ul style='margin-bottom:8px'>" +
          remarks.map((r) => "<li>" + this.escape_html(r) + "</li>").join("") +
          "</ul>";
      }
      let x = await this.$confirm({
        title: "Ready for Pickup",
        message:
          "Mark this unit as Ready to Pick-up? The owner will be notified via text message.<br><br>" +
          details,
        color: "success",
        icon: "mdi-thumb-up",
        width: 560,
      });
      if (!x) return;
      this.pickup_id = item.id;
      try {
        await this.ready_for_pickup_unit(item);
        this.$alert({
          title: "Success",
          message: "Unit is now marked as Ready to Pick-up",
          color: "success",
          icon: "mdi-check-circle",
        });
      } finally {
        this.pickup_id = null;
      }
    },
    clear_dates() {
      this.date_from = "";
      this.date_to = "";
    },
    // The API returns created_at/updated_at as formatted strings ("September 9,
    // 2024", see Problem model accessors), so parse to a comparable YYYYMMDD
    // number instead of string-slicing. Returns null when unparseable.
    to_day_number(value) {
      if (!value) return null;
      let d = new Date(value);
      if (isNaN(d.getTime())) return null;
      return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
    },
    filter_by_date(list) {
      if (!Array.isArray(list)) return [];
      if (!this.date_from && !this.date_to) return list;
      let from = this.date_from
        ? Number(this.date_from.replace(/-/g, ""))
        : null;
      let to = this.date_to ? Number(this.date_to.replace(/-/g, "")) : null;
      return list.filter((item) => {
        let d = this.to_day_number(item && item.created_at);
        if (d === null) return false;
        if (from !== null && d < from) return false;
        if (to !== null && d > to) return false;
        return true;
      });
    },
    show_problem(item) {
      this.selected_problem = item;
      this.show_problem_layout = true;
    },
    open_problem_dialog(item) {
      this.selected_item = item;
      this.set_problem(item).then(() => {
        this.problem_dialog = true;
        console.log(this.selected_item.id);
      });
    },
    refresh() {
      this.loading = true;
      Promise.all([
        this.get_pending_units(),
        this.get_wip_units(),
        this.get_pickup_units(),
        this.get_cancelled_units(),
      ]).then(() => {
        this.loading = false;
      });
    },
  },
  computed: {
    ...mapGetters({
      problem: "problem/problem",
      pending_units: "unit/pending_units",
      wip_units: "unit/wip_units",
      pickup_units: "unit/pickup_units",
      cancelled_units: "unit/cancelled_units",
      user: "auth/user",
      hasPermission: "permission/hasPermission",
    }),
    // Hat-based: the Start Working action key, not the legacy Technician/Admin role.
    can_start_working() {
      return this.hasPermission("units.start_working");
    },
    filtered_pending_units() {
      return this.filter_by_date(this.pending_units);
    },
    filtered_wip_units() {
      return this.filter_by_date(this.wip_units);
    },
    filtered_pickup_units() {
      return this.filter_by_date(this.pickup_units);
    },
    filtered_cancelled_units() {
      return this.filter_by_date(this.cancelled_units);
    },
  },
  created() {
    if (this.pending_units.length <= 0) {
      this.get_pending_units();
    }
    if (this.wip_units.length <= 0) {
      this.get_wip_units();
    }
    if (this.pickup_units.length <= 0) {
      this.get_pickup_units();
    }
    if (this.cancelled_units.length <= 0) {
      this.get_cancelled_units();
    }
    this.ready = true;
  },
};
</script>

<style lang="scss" scoped></style>
